export type * from "./types";
export * from "./src/emitter";

export const constUnknownToNumber = <T extends number>(input: `${T}` | T): T => typeof input === "string" ? constToNumber<T>(input) : input;
export const constToNumber = <T extends number>(input: `${T}`): T => (toNumber(input) ?? throwExpression(new RangeError())) as T;
export const toNumber = (input: string): number | undefined =>
{
	if (input.trim() === "")
	{
		return undefined;
	}

	// eslint-disable-next-line no-restricted-syntax -- is toNumber
	const parsed = Number(input);

	if (isNaN(parsed))
	{
		return undefined;
	}
	else
	{
		return parsed;
	}
};

export const isObject = (obj: unknown): obj is NonNullable<typeof obj> =>
{
	return typeof obj === "object" && !Array.isArray(obj) && obj !== null && !(obj instanceof Date);
};

/**
 * Performs a deep equality check between two values to determine if they are equivalent.
 *
 * This function compares both primitive and complex data structures, including arrays and objects,
 * by recursively checking their properties and elements for equality. It handles cases where
 * the values may be `null` or of different types, ensuring a thorough comparison.
 *
 * @param value1 - The first value to compare, which can be of any type.
 * @param value2 - The second value to compare, which can be of any type.
 * @returns A boolean indicating whether the two values are deeply equal.
 */
export const deepEqual = (value1: unknown, value2: unknown): boolean =>
{
	if (value1 === value2) return true;

	if (value1 == null || typeof value1 !== "object" || value2 == null || typeof value2 !== "object")
	{
		return false;
	}

	const isArray1 = Array.isArray(value1);
	const isArray2 = Array.isArray(value2);

	if (isArray1 !== isArray2) return false;

	if (isArray1 && isArray2)
	{
		if (value1.length !== value2.length) return false;

		return value1.every((item, index) => deepEqual(item, value2[index]));
	}
	else
	{
		const keys1 = Object.keys(value1);
		const keys2 = Object.keys(value2);

		if (keys1.length !== keys2.length) return false;

		const keys2Set = new Set(keys2);

		return keys1.every(key => keys2Set.has(key) && deepEqual((value1 as { [key: string]: unknown })[key], (value2 as { [key: string]: unknown })[key]));
	}
};

export const savedObjectTypeCheck = <T>(obj: T): Partial<NonNullable<typeof obj>> =>
{
	if (!(obj instanceof kendo.data.ObservableObject || isObject(obj)))
	{
		return {};
	}
	return obj;
};
export const savedArrayTypeCheck = <T>(arr?: T[]): NonNullable<typeof arr> =>
{
	if (!(arr instanceof kendo.data.ObservableArray || Array.isArray(arr)))
	{
		return [];
	}
	return arr;
};

type ReplaceType<TSearch extends string, TInput extends string, TReplace extends string> =
	TInput extends `${infer TStart}${TSearch}${infer TEnd}` ?
	`${TStart}${TReplace}${TEnd}` : TInput;

export const constReplace = <TInput extends string, TSearch extends string, TReplace extends string>(input: TInput, search: TSearch, replace: TReplace): ReplaceType<typeof search, typeof input, typeof replace> =>
{
	return input.replace(search, replace) as ReplaceType<typeof search, typeof input, typeof replace>;
};

export const throwExpression = (expression: Error): never =>
{
	throw expression;
};

export const getObjectKeys = <T extends object>(object: T): (keyof T)[] =>
{
	return Object.keys(object) as unknown as (keyof T)[];
};

export const getObjectValues = <T extends object>(object: T): T[keyof T][] =>
{
	return Object.values(object) as unknown as T[keyof T][];
};

export const getObjectEntries = <T extends object>(object: T): [keyof T, T[keyof T]][] =>
{
	return Object.entries(object) as [keyof T, T[keyof T]][];
};
export const getObjectKeyByValue = <T extends object>(object: T, value: T[keyof T]): keyof T =>
{
	for (const [key, val] of getObjectEntries(object))
	{
		if (val === value)
		{
			return key;
		}
	}
	throw new RangeError(`${ String(value) } is not a value of the object's entries!`);
};

export const getPropertyKey = <T>(key: keyof T): keyof T => key;

//#region Throttle
/**
 * The `throttle` function limits the execution rate of a given function.
 * It ensures that the function is not called more frequently than a specified time interval (`delay`).
 *
 * @template TArgs - The types of arguments that the function `fn` accepts.
 * @template TReturn - The return value type of the function `fn`.
 *
 * @param {(...args: TArgs) => Promise<TReturn> | TReturn} fn - The function to be throttled.
 *        It can be either synchronous or asynchronous and takes any arguments.
 * @param {number} delay - The minimum time in milliseconds between two consecutive calls of the function `fn`.
 *
 * @returns {typeof throttled} A new function implementing the throttling behavior.
 *          This function also has a `cancel` method to clear scheduled calls.
 *
 * @example
 * // Example function to be throttled
 * const logMessage = (message: string) => {
 *   console.log(`${new Date().toISOString()}: ${message}`);
 * };
 *
 * // Create a throttled version of logMessage with a delay of 2000 ms (2 seconds)
 * const throttledLogMessage = throttle(logMessage, 2000);
 *
 * // Use the throttled function
 * throttledLogMessage("Hello, World!"); // Executes immediately
 * setTimeout(() => throttledLogMessage("This will be delayed"), 1000); // Scheduled after 1 second, but actually executed after 2 seconds
 * setTimeout(() => throttledLogMessage("Another message"), 3000); // Executes after 4 seconds
 *
 * // Optional: Cancel a scheduled call
 * throttledLogMessage.cancel();
 */
export const throttle = <TArgs extends unknown[], TReturn>(fn: (...args: TArgs) => Promise<TReturn> | TReturn, delay: number): typeof throttled =>
{
	let timeout: ReturnType<typeof setTimeout> | undefined;
	let lastExecTime = 0;

	if (delay <= 0)
	{
		throw new RangeError(`delay value is not positive: ${ delay.toString() }!`);
	}

	const throttled = async (...args: TArgs) =>
	{
		const elapsed = Date.now() - lastExecTime;

		/* function to execute the original function and update last execution time */
		const exec = async () =>
		{
			lastExecTime = Date.now();
			return fn(...args);
		};

		/* first execution */
		if (lastExecTime === 0)
		{
			return exec();
		}

		/* clear any existing timeout */
		if (timeout !== undefined)
		{
			clearTimeout(timeout);
		}

		/* if enough time has elapsed, execute immediately */
		if (elapsed > delay)
		{
			return exec();
		}
		else
		{
			/* schedule execution after the remaining time */
			return new Promise<TReturn>((resolve) =>
			{
				timeout = setTimeout(() =>
				{
					resolve(exec());
				}, delay - elapsed);
			});
		}
	};

	/** Cancels any scheduled execution of the throttled function. */
	throttled.cancel = () =>
	{
		if (timeout !== undefined)
		{
			clearTimeout(timeout);
		}
	};

	return throttled;
};
//#endregion

export const copyToClipboard = async (text: string): Promise<void> =>
{
	await navigator.clipboard.writeText(text);
};

export const createObjectURL = (blob: Blob): {
	readonly url: string;
	readonly revoke: () => void;
} =>
{
	const url = URL.createObjectURL(blob);

	return {
		url,
		revoke: () => URL.revokeObjectURL(url)
	};
};

export const startResponseDownload = async (response: Response, fileName: string): Promise<void> =>
{
	const result = createObjectURL(await response.blob());
	const a		= document.createElement("a");
	a.href		= result.url;
	a.download	= fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
	result.revoke();
};

export const arrayMapAdd = <TKey, TValue>(map: Map<TKey, TValue[]>, key: TKey, values: TValue[]): void =>
{
	const mapValue = map.get(key);

	if (mapValue != null)
	{
		mapValue.push(...values);
	}
	else
	{
		map.set(key, values);
	}
};

export const groupBy = <T, TKey>(array: T[], predicate: (value: T, index: number, array: T[]) => TKey): Map<TKey, T[]> =>
{
	return array.reduce((map, value, index, ar) =>
	{
		const key = predicate(value, index, ar);

		arrayMapAdd(map, key, [value]);

		return map;
	}, new Map<TKey, T[]>());
};

export const undefinedIfEmptyObject = <T extends Record<string, unknown>>(obj: T): T | undefined =>
{
	return Object.entries(obj).filter(([,value]) => value != null).length > 0 ? obj : undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- unknown does not allow property assignment
export const removeUndefinedProperties = <T extends Record<string, any>>(obj: T): T =>
{
	return getObjectKeys(obj).reduce((acc, key) =>
	{
		if (obj[key] !== undefined)
		{
			const value = isObject(obj[key]) ? removeUndefinedProperties(obj[key]) : obj[key];

			if (!(isObject(value) && Object.keys(value).length === 0))
			{
				acc[key] = value;
			}
		}

		return acc;
	}, {} as T);
};

export const delay = async (milliseconds: number): Promise<void> =>
{
	return new Promise((resolve) =>
	{
		setTimeout(resolve, milliseconds);
	});
};

export const defer = async (): Promise<void> =>
{
	await delay(1);
};

export const nameofFactory = <T>() => <TKey extends keyof T>(name: TKey): TKey => name;

/**
 * Encodes a given Unicode string into a base64 format.
 *
 * Encoding text that includes special or non-ASCII characters
 * to ensure safe transmission over systems that only support ASCII.
 *
 * @param text - The input string to encode into base64 format.
 * @returns A base64 encoded string representation of the input text.
 * @throws error indicating the failure of encoding.
 *
 * Usage Example:
 * ```typescript
 * const originalText = "Hello, 世界";
 * const encodedText = b64EncodeUnicode(originalText);
 * console.log(encodedText); // Outputs the base64 encoded string
 * ```
 */
export const b64EncodeUnicode = (text: string): string =>
{
	try
	{
		const percentEncodedText = encodeURIComponent(text);
		const decodedText = percentEncodedText.replace(/%([0-9A-F]{2})/g, (_, p1) =>
		{
			// eslint-disable-next-line no-restricted-globals -- ok
			return String.fromCharCode(parseInt(p1, 16));
		});
		return btoa(decodedText);
	}
	catch (error)
	{
		console.error("Failed to encode text to base64:", error);
		throw new Error("Encoding failed");
	}
};

/**
 * Decodes a base64 encoded string back into its original Unicode form.
 *
 * Decoding text that was previously encoded in base64 format
 * and includes special or non-ASCII characters.
 *
 * @param text - The base64 encoded string to decode.
 * @returns The original Unicode string representation of the input base64 string.
 *
 * @throws error indicating the failure of decoding.
 *
 * @example
 * // Base64 encoded string for "Hello, World!"
 * const encodedText = "SGVsbG8sIFdvcmxkIQ==";
 * const decodedText = b64DecodeUnicode(encodedText);
 * console.log(decodedText); // Output: "Hello, World!"
 */
export const b64DecodeUnicode = (text: string): string =>
{
	try
	{
		const binaryString = atob(text);
		const percentEncodedString = Array.from(binaryString, (c) =>
			"%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
		).join("");
		return decodeURIComponent(percentEncodedString);
	}
	catch (error)
	{
		console.error("Failed to decode base64 text:", error);
		throw new Error("Decoding failed");
	}
};

/**
 * Converts an ArrayBuffer or an array of numbers into a Base64 encoded string.
 *
 * @param buffer - The input buffer, which can be either an ArrayBuffer or an array of numbers.
 * @returns A Base64 encoded string representing the input buffer.
 */
export const arrayBufferToBase64 = (buffer: ArrayBuffer | number[]): string =>
{
	let binary = "";
	const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer);
	for (let i = 0; i < bytes.byteLength; i++)
	{
		binary += String.fromCharCode(bytes[i]!);
	}
	return btoa(binary);
};

export const escapeHtml = (text: string): string =>
{
	const map = {
		"&"	: "&amp;",
		"<"	: "&lt;",
		">"	: "&gt;",
		"\"": "&quot;",
		"'"	: "&#039;"
	} as const;
	return text.replace(/[&<>"']/g, (m) => map[m as keyof typeof map]);
};

export const unescapeHtml = (text: string): string =>
{
	const map = {
		"&amp;"	: "&",
		"&lt;"	: "<",
		"&gt;"	: ">",
		"&quot;": "\"",
		"&#039;": "'"
	} as const;
	return text.replace(/&(amp|lt|gt|quot|#039);/g, (m) => map[m as keyof typeof map]);
};

/**
 * Converts a number of bytes into a human-readable string using either binary or decimal units.
 *
 * @param {number} bytes - The number of bytes to be converted.
 * @param {object} [options] - Optional settings for conversion:
 *   @param {boolean} [options.useBinaryUnits=false] - Use binary units (base 1024) instead of decimal units (base 1000).
 *   @param {number} [options.roundingPrecision=2] - Number of decimal places to round the result to.
 *   @param {object} [options.localizeOptions={language: "en"}] - Localization options:
 *     @param {string} options.localizeOptions.language - Language code for localization, defaults to "en".
 *     @param {Partial<Record<Intl.LDMLPluralRule, string>>} [options.localizeOptions.plurals] - Custom plural forms.
 *
 * @returns {string} A formatted string representing the converted byte value with the appropriate unit.
 *
 * @example
 * // Convert 2048 bytes using binary units with default precision
 * convertBytes(2048, { useBinaryUnits: true }); // Returns: "2.00 KiB"
 *
 * @example
 * // Convert 1500 bytes using decimal units and 1 decimal place
 * convertBytes(1500, { roundingPrecision: 1 }); // Returns: "1.5 KB"
 *
 * @example
 * // Convert 1024 bytes with Spanish localization
 * convertBytes(1024, { localizeOptions: { language: 'es' } }); // Returns: "1.00 KiB"
 */
export const convertBytes = (
	bytes: number,
	options: {
		// Use binary units (1024) instead of decimal units (1000). Defaults to false.
		useBinaryUnits?: boolean;
		// Number of decimal places to round the result to. Defaults to 2.
		roundingPrecision?: number;
		// Localize byte units to a specific language with optional plural forms. Defaults to English, which only requires "one" and "other".
		localizeOptions?:
		| { language: "en"; plurals?: Partial<Pick<Record<Intl.LDMLPluralRule, string>, "one" | "other">> }
		// Not all languages require all plural forms, so missing forms will fall back to default plurals.
		// To check which plural forms a language needs, refer to:
		// @see: https://www.unicode.org/cldr/charts/46/supplemental/language_plural_rules.html
		| { language: string; plurals?: Partial<Record<Intl.LDMLPluralRule, string>> };
	} = { }
): string =>
{
	const { useBinaryUnits = false, roundingPrecision = 2, localizeOptions = { language: "en" } } = options;

	const DEFAULT_PLURALS: Record<Intl.LDMLPluralRule, string> = {
		zero	: "Bytes",
		one		: "Byte",
		two		: "Bytes",
		few		: "Bytes",
		many	: "Bytes",
		other	: "Bytes"
	};

	const SIZE_UNITS_BINARY		= ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
	const SIZE_UNITS_DECIMAL	= ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

	// Ensure rounding precision is valid (non-negative).
	if (roundingPrecision < 0)
	{
		throw new Error(`Invalid decimal precision: ${ roundingPrecision }`);
	}

	// Choose the correct base (1024 for binary, 1000 for decimal) and units.
	const base	= useBinaryUnits ? 1024 : 1000;
	const units	= useBinaryUnits ? SIZE_UNITS_BINARY : SIZE_UNITS_DECIMAL;

	// Combine custom plural forms with the default ones to ensure all necessary forms are included.
	const plurals = { ...DEFAULT_PLURALS, ...localizeOptions.plurals };

	// Determine the plural form based on the byte count.
	const pluralRules		= new Intl.PluralRules(localizeOptions.language);
	const pluralForm		= pluralRules.select(bytes);
	const pluralizedUnit	= plurals[pluralForm];

	// Special case for 0 bytes.
	const exponent = bytes === 0 ? 0 : Math.floor(Math.log(bytes) / Math.log(base));

	// Calculate the value and round to the specified precision.
	const value = (bytes / base ** exponent).toFixed(roundingPrecision);

	// Select the appropriate unit for the result (e.g., "KiB" or "KB").
	const unit = exponent === 0 ? pluralizedUnit : units[exponent];

	return `${value} ${unit}`;
};